/**
 * timeSlotExtension — `IMailEditorInsert.timeSlotSpace(height)` 의 NgwEditor extension (PR-N5).
 *
 * TinyMCE 평행: `apps/owma/public/editor/nine_tinymce_desktop.js` L7204~L7208 의
 * `window.timeSlotSpace(height)` 가 vertical spacer HTML 단편 (table > tbody > tr >
 * td[height=N][style="line-height: 0; font-size: 0"]) 을 반환. NgwEditor 측에서는 ProseMirror
 * leaf atom 노드로 동일 시맨틱 — height attribute 만 유지한다.
 *
 * ### parseDOM
 *
 * 신규 마커 `div.nine-time-slot-space[data-height]` + legacy spacer table 호환 마커
 * `td[style*="line-height: 0"]`. legacy 는 본래 `addSendAvailability` 컨테이너 안에서만 사용되어
 * 단독 raw HTML 직접 발생은 드물지만, PR-N7 의 feature flag 전환 후 임시저장된 답장 HTML 의
 * 라운드트립 시점에 매칭 가능성 대비.
 *
 * ### 보안
 *
 * height 는 `Number(...)` 변환 + finite 체크. NaN / Infinity 입력은 0 fallback. 노드 attr 자체가
 * inline style 의 `${height}px` 안으로만 흐르므로 raw HTML 주입 표면 없음.
 */

import type { NgwExtension } from '@ngweditor/react'

/** 인자 → height(number) 변환. 잘못된 입력은 0 fallback. */
function extractHeight(arg: unknown): number {
  if (typeof arg === 'number') {
    return Number.isFinite(arg) === true ? arg : 0
  }
  if (typeof arg === 'object' && arg !== null && 'height' in arg) {
    const h = Number((arg as { height?: unknown }).height ?? 0)
    return Number.isFinite(h) === true ? h : 0
  }
  return 0
}

export const timeSlotExtension: NgwExtension = {
  name: 'time-slot',

  nodes: {
    time_slot_space: {
      group: 'block',
      atom: true,
      selectable: false,
      attrs: {
        height: { default: 0 },
      },
      parseDOM: [
        // 신규 마커
        {
          tag: 'div.nine-time-slot-space',
          getAttrs: (dom) => {
            if (typeof dom === 'string') return false
            const raw = Number(dom.getAttribute('data-height') ?? '0')
            return { height: Number.isFinite(raw) === true ? raw : 0 }
          },
        },
        // legacy spacer (nine_tinymce_desktop.js L7204~L7208) — td[height][style="line-height: 0"]
        {
          tag: 'td[style*="line-height: 0"]',
          getAttrs: (dom) => {
            if (typeof dom === 'string') return false
            const raw = Number(dom.getAttribute('height') ?? '0')
            if (Number.isFinite(raw) === false || raw === 0) return false
            return { height: raw }
          },
        },
      ],
      toDOM: (node) => {
        const height = Number(node.attrs.height ?? 0)
        const safe = Number.isFinite(height) === true ? height : 0
        return [
          'div',
          {
            class: 'nine-time-slot-space',
            'data-height': String(safe),
            style: `height: ${safe}px; line-height: 0; font-size: 0;`,
          },
        ]
      },
    },
  },

  commands: {
    insertTimeSlotSpace: (state, dispatch, _view, ...args) => {
      if (dispatch == null) return true

      const height = extractHeight(args[0])
      const nodeType = state.schema.nodes.time_slot_space
      if (nodeType == null) return false

      const node = nodeType.create({ height })
      const tr = state.tr.replaceSelectionWith(node)
      dispatch(tr.scrollIntoView())
      return true
    },
  },
}
